import { cn } from '@/shared/lib/cn';
import { CourtIcon } from './SportIcon';

export interface ScheduleCourt {
  id: string;
  name: string;
  /** Rodzaj nawierzchni pod nazwa kortu. */
  surface?: string;
}

/** Kafelek meczu na osi — nałożony proporcjonalnie do minut startu i końca. */
export interface ScheduleMatchTile {
  kind: 'match';
  courtId: string;
  /** Start w minutach od północy. */
  startMinutes: number;
  /** Koniec w minutach od północy (start + szacowany czas meczu). */
  endMinutes: number;
  home: string;
  away: string;
  /** Podpis kafelka, np. „09:00–10:20”. */
  note?: string;
  emphasis?: 'live' | 'soon' | 'none';
  /** Identyfikator meczu — umozliwia klikniecie kafelka (`onMatchClick`). */
  matchId?: string;
}

/** Kafelek przerwy technicznej kortu. */
export interface ScheduleBreakTile {
  kind: 'break';
  courtId: string;
  startMinutes: number;
  endMinutes: number;
  /** Zakres przerwy, np. „08:15–08:45”. */
  label: string;
}

export type ScheduleTile = ScheduleMatchTile | ScheduleBreakTile;

/** Os czasu dnia: zakres pelnych godzin i kafelki nalozone co do minuty. */
export interface ScheduleTimeline {
  /** Poczatek osi w minutach od polnocy — pelna godzina. */
  startMinutes: number;
  /** Koniec osi w minutach od polnocy — pelna godzina. */
  endMinutes: number;
  tiles: ScheduleTile[];
  /** Pozycja znacznika „TERAZ” w minutach; `null` poza dniem lub zakresem. */
  nowMinutes: number | null;
}

export interface ScheduleGridProps {
  courts: readonly ScheduleCourt[];
  timeline: ScheduleTimeline;
  /** Klik w kafelek meczu (z `matchId`) — np. przejscie do wpisania wyniku. */
  onMatchClick?: (matchId: string) => void;
  className?: string;
}

/** Wysokosc jednej godziny osi — 60 minut = 5rem, wiec 1 minuta = 1/12 rem. */
const HOUR_REM = 5;

/** Minimalna wysokosc kafelka — krotki mecz musi zmiescic chociaz jedna linie. */
const MIN_TILE_REM = 1.5;

/** Prog, ponizej ktorego kafelek sklada nazwiska do jednej linii. */
const COMPACT_TILE_REM = 3.25;

const remOf = (minutes: number) => (minutes / 60) * HOUR_REM;

/** Minuty od polnocy na etykiete `HH:mm`. */
function minutesLabel(total: number): string {
  const hours = String(Math.floor(total / 60) % 24).padStart(2, '0');
  const minutes = String(total % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
}

const emphasisClasses = {
  live: 'border-success-100 bg-success-50',
  soon: 'border-warning-100 bg-warning-50',
  none: 'border-border bg-surface',
} as const;

const noteClasses = {
  live: 'text-success-600',
  soon: 'text-warning-600',
  none: 'text-fg-subtle',
} as const;

/** Pojedynczy kafelek osi — pozycja i wysokosc wprost z minut. */
function Tile({
  tile,
  axisStart,
  onMatchClick,
}: {
  tile: ScheduleTile;
  axisStart: number;
  onMatchClick?: ((matchId: string) => void) | undefined;
}) {
  const heightRem = Math.max(remOf(tile.endMinutes - tile.startMinutes), MIN_TILE_REM);
  const style = {
    top: `${remOf(tile.startMinutes - axisStart)}rem`,
    height: `${heightRem}rem`,
  };

  if (tile.kind === 'break') {
    return (
      <div
        style={style}
        className={cn(
          'absolute inset-x-1 z-[5] flex flex-col items-center justify-center overflow-hidden rounded-lg border border-border text-center text-fg-subtle',
          // Ukosne paski jako tlo — odpowiednik zakreskowanego pola z makiety.
          'bg-[repeating-linear-gradient(45deg,var(--color-surface-sunken)_0_6px,transparent_6px_12px)]',
        )}
      >
        <span className="text-[0.625rem] font-medium">Przerwa techniczna</span>
        <span className="text-[0.625rem]">{tile.label}</span>
      </div>
    );
  }

  const emphasis = tile.emphasis ?? 'none';
  const compact = heightRem < COMPACT_TILE_REM;
  const matchId = tile.matchId;
  const clickable = Boolean(matchId && onMatchClick);

  const content = compact ? (
    <span className="truncate text-xs font-medium text-fg-strong">
      {tile.home} – {tile.away}
    </span>
  ) : (
    <>
      <span className="line-clamp-1 text-xs font-medium text-fg-strong">{tile.home}</span>
      <span className="line-clamp-1 text-xs font-medium text-fg-strong">{tile.away}</span>
      {tile.note && (
        <span className={cn('text-[0.625rem] font-semibold', noteClasses[emphasis])}>
          {tile.note}
        </span>
      )}
    </>
  );

  const tileClasses = cn(
    'absolute inset-x-1 z-[5] flex flex-col items-center justify-center overflow-hidden rounded-lg border px-2 text-center',
    emphasisClasses[emphasis],
  );

  if (clickable) {
    return (
      <button
        type="button"
        style={style}
        title={`${tile.home} vs ${tile.away}`}
        onClick={() => onMatchClick!(matchId!)}
        className={cn(
          tileClasses,
          'cursor-pointer transition-shadow hover:shadow-sm',
          'focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none',
        )}
      >
        {content}
      </button>
    );
  }
  return (
    <div style={style} title={`${tile.home} vs ${tile.away}`} className={tileClasses}>
      {content}
    </div>
  );
}

/**
 * Proporcjonalna os czasu kortow: korty w kolumnach, pelne godziny na osi
 * z lewej, a kafelki meczow i przerw nalozone dokladnie wedlug minut —
 * mecz 9:00 na 80 minut zajmuje pas od 9:00 do 10:20.
 *
 * Na waskich ekranach przewija sie poziomo, a kolumna z godzinami zostaje
 * przyklejona do lewej krawedzi, zeby nie zgubic kontekstu.
 */
export function ScheduleGrid({ courts, timeline, onMatchClick, className }: ScheduleGridProps) {
  const templateColumns = `4.5rem repeat(${courts.length}, minmax(10rem, 1fr))`;
  const totalRem = remOf(timeline.endMinutes - timeline.startMinutes);

  const hourMarks: number[] = [];
  for (let minutes = timeline.startMinutes; minutes <= timeline.endMinutes; minutes += 60) {
    hourMarks.push(minutes);
  }

  const nowOffsetRem =
    timeline.nowMinutes !== null ? remOf(timeline.nowMinutes - timeline.startMinutes) : null;

  return (
    <div
      className={cn('w-full overflow-x-auto rounded-xl border border-border bg-surface', className)}
    >
      <div className="min-w-max">
        {/* Naglowek z kortami */}
        <div
          className="grid border-b border-border"
          style={{ gridTemplateColumns: templateColumns }}
        >
          <div className="sticky left-0 z-10 bg-surface" />
          {courts.map((court) => (
            <div key={court.id} className="flex items-center justify-center gap-2 px-3 py-3">
              <CourtIcon className="size-4 text-fg-subtle" />
              <div className="min-w-0">
                <p className="truncate text-[0.8125rem] font-bold text-fg-strong">{court.name}</p>
                {court.surface && (
                  <p className="truncate text-[0.625rem] text-fg-subtle">{court.surface}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Os proporcjonalna — pionowy pas dnia */}
        <div className="grid py-3" style={{ gridTemplateColumns: templateColumns }}>
          {/* Rynna godzin — pelne godziny przy liniach siatki */}
          <div
            className="sticky left-0 z-10 bg-surface"
            style={{ height: `${totalRem}rem`, position: 'sticky' }}
          >
            <div className="relative h-full">
              {hourMarks.map((minutes) => (
                <span
                  key={minutes}
                  className="absolute right-2 -translate-y-1/2 text-sm text-fg-muted tabular-nums"
                  style={{ top: `${remOf(minutes - timeline.startMinutes)}rem` }}
                >
                  {minutesLabel(minutes)}
                </span>
              ))}
              {nowOffsetRem !== null && (
                <span
                  className="absolute right-2 -translate-y-full text-[0.625rem] font-bold tracking-[0.08em] text-success-600 uppercase"
                  style={{ top: `${nowOffsetRem}rem` }}
                >
                  Teraz
                </span>
              )}
            </div>
          </div>

          {/* Kolumny kortow z liniami godzin i kafelkami */}
          {/* Kolumny kortow bez poziomych linii godzin — orientacje czasowa
              daje sama rynna z etykietami, a kafelki niosa dokladne zakresy. */}
          {courts.map((court) => (
            <div
              key={court.id}
              className="relative border-l border-border"
              style={{ height: `${totalRem}rem` }}
            >
              {nowOffsetRem !== null && (
                <div
                  aria-hidden="true"
                  className="absolute inset-x-0 z-10 h-px bg-success-600"
                  style={{ top: `${nowOffsetRem}rem` }}
                />
              )}

              {timeline.tiles
                .filter((tile) => tile.courtId === court.id)
                .map((tile) => (
                  <Tile
                    key={`${tile.kind}-${tile.courtId}-${tile.startMinutes}-${
                      tile.kind === 'match' ? (tile.matchId ?? tile.home) : 'break'
                    }`}
                    tile={tile}
                    axisStart={timeline.startMinutes}
                    onMatchClick={onMatchClick}
                  />
                ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
